// Shared support UI pieces (SUP-01): state badge, message bubble, fields, dialog, knowledge form.
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Dialog, DialogContent, Button } from '@mui/material';
import { SYSTEM_STACK, btnPrimarySx as basePrimarySx, btnSecondarySx as baseSecondarySx } from '../journalDesign';
import { STATE_LABELS } from './supportApi';

const TONES = {
  AI_ACTIVE: ['#0A84FF', 'rgba(10,132,255,0.12)'],
  HUMAN_REQUESTED: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  HUMAN_ACTIVE: ['#8E5CF7', 'rgba(142,92,247,0.14)'],
  RESOLVED: ['#6E6E73', 'rgba(142,142,147,0.16)'],
  CLOSED: ['#6E6E73', 'rgba(142,142,147,0.16)'],
  DRAFT: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  APPROVED: ['#0A84FF', 'rgba(10,132,255,0.12)'],
  ARCHIVED: ['#6E6E73', 'rgba(142,142,147,0.16)'],
  OPEN: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  RETURNED_TO_AI: ['#0A84FF', 'rgba(10,132,255,0.12)'],
  IGNORED: ['#6E6E73', 'rgba(142,142,147,0.16)'],
  sent: ['#0A84FF', 'rgba(10,132,255,0.12)'],
  failed: ['#D70015', 'rgba(255,69,58,0.14)'],
  pending: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  sending: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  unreviewed: ['#C27C0E', 'rgba(255,159,10,0.16)'],
  approved: ['#0A84FF', 'rgba(10,132,255,0.12)'],
  corrected: ['#8E5CF7', 'rgba(142,92,247,0.14)'],
  flagged: ['#D70015', 'rgba(255,69,58,0.14)'],
  high: ['#D70015', 'rgba(255,69,58,0.14)'],
};

export function Badge({ value, label }) {
  const [fg, bg] = TONES[value] || ['#6E6E73', 'rgba(142,142,147,0.16)'];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px',
      borderRadius: '10px', fontSize: 11, fontWeight: 600, color: fg, bgcolor: bg, whiteSpace: 'nowrap' }}>
      {label || STATE_LABELS[value] || value}
    </Box>
  );
}

export const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// "Where to go" button under an answer. Only in-app paths reach here (validated on the knowledge entry), so it is a
// router link: no page reload, never off-site.
export function PageLink({ T, link }) {
  if (!link || typeof link.url !== 'string' || !link.url.startsWith('/') || link.url.startsWith('//')) return null;
  return (
    <Box component={RouterLink} to={link.url}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '12px', py: '6px', borderRadius: '14px',
        fontSize: 13, fontWeight: 600, textDecoration: 'none', color: '#0A84FF', bgcolor: 'rgba(10,132,255,0.10)',
        border: '1px solid rgba(10,132,255,0.25)', transition: 'background-color .15s ease-out',
        '&:hover': { bgcolor: 'rgba(10,132,255,0.18)' }, '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: 2 } }}>
      {link.label || 'Open page'} <span aria-hidden="true">→</span>
    </Box>
  );
}

// Screenshots the support team sent (served by the backend at /api/support/images/<uuid>.png). Tap opens full size.
export function SupportImages({ images, max = '78%' }) {
  const list = Array.isArray(images) ? images.filter((u) => typeof u === 'string' && u.startsWith('/api/support/images/')) : [];
  if (!list.length) return null;
  const base = '';   // same origin: nginx (prod) and the dev server proxy /api
  return (
    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '6px', maxWidth: max }}>
      {list.map((u) => (
        <Box key={u} component="a" href={base + u} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', lineHeight: 0 }}>
          <Box component="img" src={base + u} alt="Screenshot from the support team" loading="lazy"
            sx={{ maxWidth: 220, maxHeight: 220, borderRadius: '12px', border: '1px solid rgba(127,127,127,0.25)', objectFit: 'cover' }} />
        </Box>
      ))}
    </Box>
  );
}

// Common in-app pages offered when picking a knowledge link (any /path is allowed).
export const APP_PAGES = ['/app', '/app/billing', '/app/settings', '/app/settings#social', '/app/settings#trading-rules',
  '/app/settings#brokers', '/app/settings#notifications', '/app/brokers', '/app/charting', '/app/trades', '/app/watchlists',
  '/app/portfolios', '/app/market-overview', '/app/rrg', '/app/analysis', '/app/insights', '/app/ai-chat', '/app/recap',
  '/app/trade-plans', '/app/playbook', '/app/upload-trades', '/app/notifications', '/app/learning', '/practice',
  '/practice/arena', '/practice/tournaments', '/forgot-password', '/pricing'];

const SENDER = { customer: 'You', ai: 'Support assistant', human: 'Support team' };

// One chat message. `admin` shows the customer's name for customer messages and review state for AI ones.
export function MessageBubble({ T, m, admin, customerName }) {
  if (m.sender_type === 'system') {
    return (
      <Typography sx={{ textAlign: 'center', fontSize: 12, color: T.muted, my: '10px', px: 2 }}>
        {m.content}
      </Typography>
    );
  }
  const mine = admin ? m.sender_type !== 'customer' : m.sender_type === 'customer';
  const human = m.sender_type === 'human';
  const name = admin && m.sender_type === 'customer' ? (customerName || 'Customer') : SENDER[m.sender_type];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', my: '6px' }}>
      <Typography sx={{ fontSize: 11, color: T.muted, mb: '3px', px: '4px' }}>
        {name}{human && !admin ? ' · a person' : ''} · {fmtTime(m.created_at)}
        {admin && m.sender_type === 'ai' && m.review_status ? <> · <Badge value={m.review_status} /></> : null}
      </Typography>
      <Box sx={{
        maxWidth: '78%', px: '14px', py: '9px', borderRadius: '16px', fontSize: 14, lineHeight: 1.45,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        bgcolor: mine ? T.btnPriBg : (human ? 'rgba(142,92,247,0.12)' : T.graphite),
        color: mine ? T.btnPriText : T.ink,
        border: human ? '1px solid rgba(142,92,247,0.35)' : '1px solid transparent',
        borderBottomRightRadius: mine ? '6px' : '16px', borderBottomLeftRadius: mine ? '16px' : '6px',
      }}>
        {m.content}
      </Box>
      <SupportImages images={m.images} />
      {Array.isArray(m.links) && m.links.length > 0 && (
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '6px', maxWidth: '78%' }}>
          {m.links.map((l) => <PageLink key={l.url} T={T} link={l} />)}
        </Box>
      )}
    </Box>
  );
}

export const inputSx = (T) => ({
  width: '100%', boxSizing: 'border-box', font: 'inherit', fontFamily: SYSTEM_STACK, fontSize: 14,
  color: T.ink, bgcolor: T.card, border: `1px solid ${T.line}`, borderRadius: '10px', px: '12px', py: '9px',
  outline: 'none', transition: 'border-color .15s ease', '&:focus': { borderColor: '#0A84FF' },
  '&::placeholder': { color: T.muted },
});

export function Field({ T, label, hint, children }) {
  return (
    <Box component="label" sx={{ display: 'block', mb: '14px' }}>
      <Typography component="span" sx={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.inkSoft, mb: '5px' }}>
        {label}
      </Typography>
      {children}
      {hint && <Typography component="span" sx={{ display: 'block', fontSize: 11.5, color: T.muted, mt: '4px' }}>{hint}</Typography>}
    </Box>
  );
}

export function TextInput({ T, multiline, rows = 3, ...props }) {
  return <Box component={multiline ? 'textarea' : 'input'} rows={multiline ? rows : undefined}
    sx={{ ...inputSx(T), resize: multiline ? 'vertical' : undefined }} {...props} />;
}

export function Select({ T, options, ...props }) {
  return (
    <Box component="select" sx={{ ...inputSx(T), py: '8px' }} {...props}>
      {options.map((o) => (typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>))}
    </Box>
  );
}

export function SheetDialog({ T, open, onClose, title, children, actions, width = 640 }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false}
      PaperProps={{ sx: { width, maxWidth: 'calc(100vw - 32px)', bgcolor: T.card, color: T.ink, borderRadius: '14px',
        backgroundImage: 'none', fontFamily: SYSTEM_STACK } }}>
      <Box sx={{ px: '22px', pt: '18px', pb: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, fontFamily: SYSTEM_STACK }}>{title}</Typography>
        <Button onClick={onClose} sx={{ ...baseSecondarySx(T), px: 1.5 }}>Close</Button>
      </Box>
      <DialogContent sx={{ px: '22px', pt: '8px' }}>{children}</DialogContent>
      {actions && <Box sx={{ px: '22px', pb: '18px', display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{actions}</Box>}
    </Dialog>
  );
}

export function ErrorLine({ T, error }) {
  if (!error) return null;
  return <Typography role="alert" sx={{ fontSize: 13, color: T.red, my: 1 }}>{error}</Typography>;
}

// Knowledge entry form (create / edit / from correction / from conversation). `value` is controlled.
export function KnowledgeForm({ T, value, onChange, categories, showStatus = true }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <Box>
      <Field T={T} label="Title"><TextInput T={T} value={value.title || ''} onChange={set('title')} /></Field>
      <Field T={T} label="Question / trigger" hint="How customers usually ask it.">
        <TextInput T={T} multiline rows={2} value={value.question || ''} onChange={set('question')} />
      </Field>
      <Field T={T} label="Approved answer" hint="Only what is true today. The assistant quotes this.">
        <TextInput T={T} multiline rows={4} value={value.answer || ''} onChange={set('answer')} />
      </Field>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
        <Field T={T} label="Where to go (in-app page)" hint="Shown as a button under the answer, e.g. /app/billing">
          <TextInput T={T} list="support-app-pages" placeholder="/app/…" value={value.link_url || ''} onChange={set('link_url')} />
          <datalist id="support-app-pages">{APP_PAGES.map((p) => <option key={p} value={p} />)}</datalist>
        </Field>
        <Field T={T} label="Button label" hint="e.g. Open Billing & plans">
          <TextInput T={T} maxLength={60} placeholder="Open page" value={value.link_label || ''} onChange={set('link_label')} />
        </Field>
      </Box>
      <Field T={T} label="Additional context (internal)">
        <TextInput T={T} multiline rows={2} value={value.context || ''} onChange={set('context')} />
      </Field>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: showStatus ? '1fr 1fr 1fr' : '1fr 1fr' }, gap: '12px' }}>
        <Field T={T} label="Category">
          <Select T={T} value={value.category || 'Other'} onChange={set('category')} options={categories} />
        </Field>
        <Field T={T} label="Tags" hint="Comma separated">
          <TextInput T={T} value={Array.isArray(value.tags) ? value.tags.join(', ') : (value.tags || '')}
            onChange={(e) => onChange({ ...value, tags: e.target.value })} />
        </Field>
        {showStatus && (
          <Field T={T} label="Status">
            <Select T={T} value={value.status || 'DRAFT'} onChange={set('status')}
              options={[{ value: 'DRAFT', label: 'Draft (not used)' }, { value: 'APPROVED', label: 'Approved (used by AI)' }]} />
          </Field>
        )}
      </Box>
    </Box>
  );
}

export const tagsToList = (tags) => (Array.isArray(tags) ? tags
  : String(tags || '').split(',').map((t) => t.trim()).filter(Boolean));

// journalDesign buttons plus a readable disabled state (MUI's default greys the label out on the dark pill).
// '&&' doubles the class specificity: the app theme's dark-mode Button colour otherwise wins and the label turns
// white on the white primary pill.
export const btnPrimarySx = (T) => ({ ...basePrimarySx(T), '&&': { color: T.btnPriText },
  '&&.Mui-disabled': { bgcolor: T.btnSecBg, color: T.muted } });
export const btnSecondarySx = (T) => ({ ...baseSecondarySx(T), '&&': { color: T.btnSecText },
  '&&.Mui-disabled': { bgcolor: T.btnSecBg, color: T.muted, opacity: 0.7 } });
